// /asistente — "Tus agentes": the client's collection of their AI agents (one
// per WhatsApp conversation). Picking one opens the ONE editor — the conversation
// flow editor at /asistente/:botId/flujos — where the whole conversation is
// designed and the agent's settings (the handoff safety net) live. The separate
// guided "Ajustar" page (greeting + questions, which edited a flow that did NOT
// drive the conversation) was retired in orchestrator M6: one surface per agent,
// not two.

import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import { getWorkers, type Worker } from './workers-data.js'
import {
  Button,
  Card,
  Chip,
  EmptyStateCard,
  PageHeader,
  SkillCardListItem,
  type SkillStatus,
  Spinner,
} from '../../design-system/index.js'
import { QueryError } from '../../lib/QueryError.js'
import { flowProvenanceMeta, ownerListFlows, type WorkerFlow } from '../admin/flows-data.js'

const STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  paused: 'Pausado',
  draft: 'En preparación',
}
const statusTone = (s: string): 'ok' | 'warn' | 'draft' =>
  s === 'active' ? 'ok' : s === 'paused' ? 'warn' : 'draft'

const RobotIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <rect x="6" y="8" width="12" height="10" rx="2" />
    <path d="M12 8V4M9 13h.01M15 13h.01" strokeLinecap="round" />
  </svg>
)

export function WorkerEditorPage(): JSX.Element {
  const {
    data: workers,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['workers'],
    queryFn: getWorkers,
  })
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div data-testid="worker-editor" className="mx-auto w-full max-w-4xl py-10">
        <Spinner label="Cargando tus agentes…" />
      </div>
    )
  }

  // A fetch error must NOT read as "you have no agent" (the empty state below) —
  // that's confidently wrong. Surface the error with a retry.
  if (isError) {
    return (
      <div data-testid="worker-editor" className="mx-auto w-full max-w-2xl py-10">
        <QueryError message="No pudimos cargar tus agentes." onRetry={() => void refetch()} />
      </div>
    )
  }

  if (!workers || workers.length === 0) {
    return (
      <div data-testid="worker-editor" className="mx-auto w-full max-w-2xl">
        <PageHeader title="Tus agentes" />
        <div className="mt-6">
          <EmptyStateCard
            icon={RobotIcon}
            title="Aún no tienes un agente configurado"
            body="Tu gerente de cuenta lo prepara contigo durante la activación. En cuanto esté listo, podrás ajustarlo aquí."
          />
        </div>
      </div>
    )
  }

  return (
    <div data-testid="worker-editor" className="mx-auto w-full max-w-4xl">
      <PageHeader
        title="Tus agentes"
        subtitle="Cada agente atiende por WhatsApp. Elige uno para diseñar su conversación y ajustar cuándo pasa con una persona."
      />
      {/* CP-2: en esta pantalla se veían flujos sueltos y no se sabía si lo que
          falta se agrega aquí o en Configuración. La frase dice de dónde salen,
          y cada flujo de abajo dice a qué proceso pertenece. */}
      <p className="mt-4 text-[13px] leading-relaxed text-text-2">
        Modelas tus procesos en{' '}
        <Link to="/configuracion?tab=embudos" className="font-semibold text-link underline">
          Configuración
        </Link>
        . Cada canal lo atiende un agente y de ahí sale su flujo.
      </p>
      <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {workers.map((w) => (
          <li key={w.id}>
            <AgentCard
              worker={w}
              onEdit={() => void navigate(`/asistente/${w.id}/flujos`)}
              onOpenFlow={(flowId) =>
                void navigate(`/asistente/${w.id}/flujos?flow=${encodeURIComponent(flowId)}`)
              }
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

/** El estado del flujo, en el vocabulario del pill. Un estado que la lista no
 *  conoce se dibuja como borrador en vez de inventar un cuarto tono. */
function flowStatus(status: string): SkillStatus {
  return status === 'active' || status === 'paused' ? status : 'draft'
}

/** La frase que el cliente lee cuando un flujo dejó de coincidir con su
 *  configuración. Se dice EN LA FILA, que es donde el flujo se ve, y lleva el
 *  camino que sí existe: se edita el proceso en Configuración y el flujo se
 *  actualiza al publicar el agente. */
const DRIFT_LINE = 'No coincide con tu configuración. Se actualiza al publicar el agente desde '

/** SE VE DE DÓNDE SALE CADA FLUJO Y SI SIGUE AL DÍA (F4-3).
 *
 *  La lista decía de qué proceso sale cada flujo y nada sobre su frescura, así
 *  que un flujo generado hace tres meses y una ficha editada ayer se veían
 *  exactamente igual. El veredicto lo calcula el servidor (`drift`), aquí sólo
 *  se pinta: un flujo sin veredicto no lleva chip, porque "no se sabe" y "al
 *  día" no se pueden ver iguales. */
function FlowDriftNote({ flow }: { flow: WorkerFlow }): JSX.Element | null {
  const state = flow.drift?.state ?? 'none'
  if (state === 'none') return null
  return (
    <p className="mt-1 flex flex-wrap items-center gap-2 px-4 text-[11.5px] leading-relaxed text-text-2">
      {state === 'drift' ? (
        <>
          <Chip tone="warn" size="sm">
            No coincide
          </Chip>
          <span>
            {DRIFT_LINE}
            <Link to="/configuracion?tab=embudos" className="font-semibold text-link underline">
              Procesos
            </Link>
            .
          </span>
        </>
      ) : state === 'synced' ? (
        <Chip tone="ok" size="sm">
          Al día con tu configuración.
        </Chip>
      ) : (
        <Chip tone="draft" size="sm">
          De fábrica: todavía no sale de tu configuración.
        </Chip>
      )}
    </p>
  )
}

function AgentCard({
  worker,
  onEdit,
  onOpenFlow,
}: {
  worker: Worker
  onEdit: () => void
  onOpenFlow: (flowId: string) => void
}): JSX.Element {
  // Los flujos de ESTE agente, para decir de qué proceso sale cada uno. Un fallo
  // aquí no rompe la tarjeta: la lista simplemente no se dibuja, porque el
  // agente y su botón siguen siendo verdad sin ella.
  const { data: flows, isError: flowsFailed } = useQuery({
    queryKey: ['worker-flows', worker.id],
    queryFn: () => ownerListFlows(worker.id),
  })
  return (
    <Card>
      <Card.Body>
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="grid size-10 flex-none place-items-center rounded-xl bg-field text-text-2 [&_svg]:size-5"
          >
            {RobotIcon}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-[14px] font-bold text-text">{worker.name}</span>
              <Chip tone={statusTone(worker.status)} dot size="sm">
                {STATUS_LABEL[worker.status] ?? worker.status}
              </Chip>
            </div>
            {worker.role ? (
              <p className="mt-0.5 truncate text-[12px] text-text-2">{worker.role}</p>
            ) : null}
            <p className="mt-0.5 text-[11.5px] text-text-3">
              {worker.number ? `WhatsApp ${worker.number}` : 'Sin número conectado'}
            </p>
          </div>
        </div>
        {flowsFailed && (
          <p className="mt-3 text-[12px] text-text-3" data-testid="agent-flows-error">
            No se pudieron cargar los flujos de este agente.
          </p>
        )}
        {flows && flows.length > 0 ? (
          <ul className="mt-3 flex flex-col gap-2">
            {flows.map((f: WorkerFlow) => (
              <li key={f.id}>
                <SkillCardListItem
                  name={f.name}
                  status={flowStatus(f.status)}
                  steps={f.flow.nodes.length}
                  meta={flowProvenanceMeta(f)}
                  onClick={() => onOpenFlow(f.id)}
                />
                <FlowDriftNote flow={f} />
              </li>
            ))}
          </ul>
        ) : null}
        <div className="mt-3 flex justify-end">
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Editar conversación
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}
